import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline, IoTrashOutline } from 'react-icons/io5';
import { BsTrash3, BsDownload } from 'react-icons/bs';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import DefaultButton from '../../../../components/Buttons/Default';
import DefaultPagination from '../../../../components/Pagination';
import { toast } from 'react-toastify';
import { del, get, getBlob } from '../../../../utils/helpers/api';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import { TfiFilter } from 'react-icons/tfi';
import FiltersDrawer from './Drawers/Filters';
import { useSettings } from '../../../../context/settings';
import AgGridTable from '../../../../components/Tables/AgGrid';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/crm/meetings/edit/${data.id}`, { state: { meetingData: data } });
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setMeetingToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function MeetingsPage() {
  const { t } = useTranslation();
  const { formatDateTime } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['keyword', 'location', 'host_id', 'participant_id', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(v => v).length;

  const getMeetings = useCallback(async () => {
    setLoading(true);
    let query = `/crm/meetings?page=${currentPage}&page_size=${pageSize}`;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    Object.entries(activeFilters).forEach(([key, value]) => {
      query += `&${key}=${encodeURIComponent(value)}`;
    });
    const data = await get(query);
    if (data?.meetings?.data) {
      setRowData(data.meetings.data);
      setTotalPages(data.meetings.last_page);
      setTotalRecords(data.meetings.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getMeetings();
  }, [getMeetings]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  }

  const exportMeetings = async () => {
    const data = await getBlob('/crm/meetings/export')
      .then(blob => {
        if (!blob) throw new Error('export failed');
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `meetings-${new Date().toISOString()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(err => {
        toast.error(t('common.somethingWentWrong'));
      });
  }

  const deleteMeeting = async () => {
    const data = await del('/crm/meetings/delete/' + meetingToDelete);
    if (data?.success) {
      toast.success(data.message || t('crm.meetingDeleted'));
      getMeetings();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setMeetingToDelete(null);
  };

  const columnDefs = useMemo(() => [
    { field: 'title', headerName: t('contact.title'), minWidth: 200, flex: 1, sortable: true, onCellClicked: (params) => navigate(`/crm/meetings/view/${params.data.id}`) },
    { field: 'time_from', headerName: t('common.from'), minWidth: 180, flex: 1, sortable: true, valueFormatter: (p) => formatDateTime(p.value) },
    { field: 'time_to', headerName: t('common.to'), minWidth: 180, flex: 1, sortable: true, valueFormatter: (p) => formatDateTime(p.value) },
    { field: 'location', headerName: t('common.location'), minWidth: 150, flex: 1, sortable: true },
    {
      field: 'host_id',
      headerName: t('crm.host'),
      minWidth: 180,
      flex: 1,
      sortable: true,
      valueFormatter: (params) => {
        return params.data?.employee?.firstname + ' ' + params.data?.employee?.lastname || '';
      }
    },
    { headerName: t('common.actions'), minWidth: 100, flex: 1, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer },
  ], [navigate, t, formatDateTime]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setMeetingToDelete }), [navigate]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('crm.meetings')} onReload={getMeetings} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/crm/meetings/create')}>
            {t('crm.newMeeting')}
          </DefaultButton>
          <Dropdown isOpen={moreOpen} toggle={() => setMoreOpen(o => !o)} size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={exportMeetings}>
                <BsDownload size={15} /> {t('common.export')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={meetingToDelete !== null}
        title={t('crm.deleteMeeting')}
        message={t('crm.deleteMeetingConfirm')}
        onConfirm={deleteMeeting}
        toggle={() => setMeetingToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default MeetingsPage;
